import { useMemo } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
	DefaultFieldTypeStatusEnum,
	EngagementDto,
	EngagementStatusDto,
	EngagementStatusEnum,
	FamilyDto,
	FieldValueInterface,
	PermissionDto,
	SurveyStatusColorEnum,
	SurveyStatusEnum,
	WorkStatusEnum,
} from '@families-link/shared';
import { EngagementsTableHandlers } from '../../components/engagements-table/engagements-table.component';
import {
	AllowedEngagementActions,
	EngagementsTableActions,
} from '../../components/engagements-table/engagements-table-actions.component';
import { Verified } from '../../../_core/constants/icons.constants';
import { Avatar, Box, Skeleton, Typography } from '@mui/material';
import { InHouseIcon, OutsourceIcon, VerifiedIcon } from '../../../_core/components/icons';
import { tableCellTypographyOverflowStyles } from '../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { compact } from 'lodash';
import { EngagementsLinguistLinkTableActions } from '../../components/engagements-table/engagements-linguist-link-table-actions.component';

const helper = createColumnHelper<EngagementDto & { createdAt: string; updatedAt: string }>();

/*
  !IMPORTANT!
  Order in property columnsList define order of columns in table.
 */

export type EngagementColumnName =
	| 'district'
	| 'title'
	| 'customId'
	| 'provider'
	| 'language'
	| 'family'
	| 'school'
	| 'program'
	| 'service'
	| 'duration'
	| 'type'
	| 'status'
	| 'surveyStatus'
	| 'isLinkedToFamily'
	| 'actions'
	| 'importedRow';

export function useEngagementsTableColumns(
	handlers: EngagementsTableHandlers,
	engagementColumnsList: EngagementColumnName[],
	allowedEngagementActions: AllowedEngagementActions[],
	isSelectionMode = false,
	isSorted = true,
	isSkeletonLoader = false,
	isLinguistLink = false,
	isAdminRoute = false
): ColumnDef<EngagementDto, string>[] {
	const { onDelete, onEdit, onView, onSendEmail, onSendSMS, onArchive, onUnarchive, onSendReminder } = handlers;

	const [t] = useTranslation();

	const columns = useMemo(
		() =>
			compact(
				engagementColumnsList.map((el: EngagementColumnName) => {
					switch (el) {
						case 'importedRow':
							return helper.accessor('importedRow', {
								header: 'Row',
								enableSorting: isSorted,
								meta: {
									flex: 0.5,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) => (
									<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
										{getValue()}
									</Typography>
								),
							}) as ColumnDef<EngagementDto, string>;
						case 'district':
							return helper.accessor('district', {
								header: 'District',
								enableSorting: isSorted,
								meta: {
									flex: 1,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) =>
									isSkeletonLoader ? (
										<Skeleton />
									) : (
										<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
											{getValue()?.name}
										</Typography>
									),
							}) as ColumnDef<EngagementDto, string>;
						case 'title':
							return helper.accessor(
								(row) =>
									row.isFromSecondary || isLinguistLink
										? row.title
										: row.customFieldsValues?.find((el) => el.fieldName === 'Title'),
								{
									header: t('engagements.columns.title'),
									enableSorting: isSorted,
									meta: {
										flex: 1.2,
										useCustomOverflowContainer: true,
									},
									cell: ({ getValue }) => {
										if (isSkeletonLoader) return <Skeleton />;

										const value = getValue();

										if (isLinguistLink || (value && typeof value === 'string')) {
											return (
												<Typography
													sx={mergeSx({ fontWeight: 500 }, tableCellTypographyOverflowStyles)}
													data-overflow="true"
												>
													{value ?? '-'}
												</Typography>
											);
										}

										const fieldValue = value as unknown as FieldValueInterface;

										return (
											<Typography
												sx={mergeSx({ fontWeight: 500 }, tableCellTypographyOverflowStyles)}
												data-overflow="true"
											>
												{fieldValue?.valuePlaceholder ? fieldValue?.valuePlaceholder : '-'}
											</Typography>
										);
									},
								}
							) as ColumnDef<EngagementDto, string>;
						case 'customId':
							return helper.accessor('customId', {
								header: t('engagements.columns.id'),
								enableSorting: isSorted,
								meta: {
									flex: 0.6,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) =>
									isSkeletonLoader ? (
										<Skeleton />
									) : (
										<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
											{getValue()}
										</Typography>
									),
							}) as ColumnDef<EngagementDto, string>;
						case 'provider':
							return helper.accessor('provider', {
								header: t('engagements.columns.providers'),
								enableSorting: isSorted,
								meta: {
									flex: 1.7,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) => {
									const imageUrl = getValue()?.imageUrl;
									const fullName = getValue()?.fullName;
									const workStatus = getValue()?.workStatus;
									const verificationType = getValue()?.verificationType;
									let workStatusIcon;
									switch (workStatus) {
										case WorkStatusEnum.InHouse:
											workStatusIcon = <InHouseIcon sx={{ fontSize: '12px', marginLeft: '8px' }} />;
											break;
										case WorkStatusEnum.Outsource:
											workStatusIcon = <OutsourceIcon sx={{ fontSize: '12px', marginLeft: '8px' }} />;
											break;
									}

									return fullName && isSkeletonLoader ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Skeleton variant="circular" width={24} height={24} />
											<Skeleton width="60%" />
											<Skeleton variant="rectangular" width={12} height={12} />
										</Box>
									) : fullName ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Box position="relative" sx={{ height: 28 }}>
												<Avatar src={imageUrl} alt={fullName} style={{ width: 24, height: 24, borderRadius: '6px' }} />
												{verificationType ? (
													<Verified style={{ position: 'absolute', bottom: 0, right: 0, height: 12, width: 12 }} />
												) : null}
											</Box>
											<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
												{fullName}
											</Typography>
											{workStatusIcon}
										</Box>
									) : isSkeletonLoader ? (
										<Skeleton />
									) : (
										<Typography>{'-'}</Typography>
									);
								},
							}) as ColumnDef<EngagementDto, string>;
						case 'language':
							return helper.accessor('language', {
								header: t('engagements.columns.language'),
								enableSorting: isSorted,
								meta: {
									flex: 0.7,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) =>
									isSkeletonLoader ? (
										<Skeleton />
									) : (
										<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
											{getValue() ? getValue() : '-'}
										</Typography>
									),
							}) as ColumnDef<EngagementDto, string>;
						case 'family':
							return helper.accessor('families', {
								header: t('engagements.columns.family'),
								enableSorting: isSorted,
								meta: {
									flex: 0.7,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) => {
									const uniqueFamilyMap = new Map();
									if (getValue()?.length && !isSkeletonLoader) {
										getValue()?.forEach((family: FamilyDto) => {
											uniqueFamilyMap.set(family?._id, family?.fullName);
										});
										return (
											<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
												{Array.from(uniqueFamilyMap.values()).join(', ')}
											</Typography>
										);
									}
									if (isSkeletonLoader) {
										return <Skeleton />;
									}
									return (
										<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
											{'-'}
										</Typography>
									);
								},
							}) as ColumnDef<EngagementDto, string>;
						case 'school':
							return helper.accessor(
								isLinguistLink
									? (row) => row.schoolName
									: (row) => row.customFieldsValues?.find((el) => el.fieldName === 'School'),
								{
									header: t('engagements.columns.school'),
									enableSorting: isSorted,
									meta: {
										flex: 0.7,
										useCustomOverflowContainer: true,
									},
									cell: ({ getValue }) => {
										if (isSkeletonLoader) return <Skeleton />;

										const value = getValue();

										if (isLinguistLink) {
											return (
												<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
													{value ?? '-'}
												</Typography>
											);
										}

										const fieldValue = value as unknown as FieldValueInterface;
										return (
											<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
												{fieldValue?.valuePlaceholder ? fieldValue?.valuePlaceholder : '-'}
											</Typography>
										);
									},
								}
							) as ColumnDef<EngagementDto, string>;

						case 'program':
							return helper.accessor(
								isLinguistLink
									? (row) => row.programName
									: (row) => row.customFieldsValues?.find((el) => el.fieldName === 'Program'),
								{
									header: t('engagements.columns.program'),
									enableSorting: isSorted,
									meta: {
										flex: 0.7,
										useCustomOverflowContainer: true,
									},
									cell: ({ getValue }) => {
										if (isSkeletonLoader) return <Skeleton />;

										const value = getValue();

										if (isLinguistLink) {
											return (
												<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
													{value ?? '-'}
												</Typography>
											);
										}

										const fieldValue = value as unknown as FieldValueInterface;
										return (
											<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
												{fieldValue?.valuePlaceholder ? fieldValue?.valuePlaceholder : '-'}
											</Typography>
										);
									},
								}
							) as ColumnDef<EngagementDto, string>;

						case 'service':
							return helper.accessor((row) => row.customFieldsValues?.find((el) => el.fieldName === 'Service'), {
								header: t('engagements.columns.service'),
								enableSorting: isSorted,
								meta: {
									flex: 0.7,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) =>
									isSkeletonLoader ? (
										<Skeleton />
									) : (
										<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
											{getValue()?.valuePlaceholder ? getValue()?.valuePlaceholder : '-'}
										</Typography>
									),
							}) as ColumnDef<EngagementDto, string>;
						case 'duration':
							return helper.accessor((row) => row.duration, {
								header: t('engagements.columns.date'),
								enableSorting: isSorted,
								meta: {
									flex: 1.4,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue, row }) => {
									const fieldDurationValue = getValue();

									const fieldTypeValue = isLinguistLink
										? row.original.schedulingType
										: row.original.customFieldsValues?.find((el) => el.fieldName === 'Type')?.value;

									return isSkeletonLoader ? (
										<Skeleton />
									) : (
										<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
											{fieldDurationValue?.startDate
												? fieldTypeValue === DefaultFieldTypeStatusEnum.Appointment
													? `${format(new Date(fieldDurationValue.startDate), 'MM.dd.yyyy')}`
													: `${format(new Date(fieldDurationValue.startDate), 'MM.dd.yyyy')}-${
															fieldDurationValue?.endDate
																? format(new Date(fieldDurationValue?.endDate), 'MM.dd.yyyy')
																: '...'
													  }`
												: '-'}
										</Typography>
									);
								},
							}) as ColumnDef<EngagementDto, string>;
						case 'status':
							return helper.accessor('statusObject', {
								header: t('engagements.columns.status'),
								enableSorting: isSorted,
								meta: {
									flex: 0.6,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) => {
									const engagementStatus = getValue() as EngagementStatusDto;

									return engagementStatus && !isSkeletonLoader ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Box
												sx={{
													height: 10,
													width: 10,
													borderRadius: '50%',
													backgroundColor: engagementStatus.color,
													flexShrink: 0,
												}}
											></Box>
											<Typography data-overflow="true" variant="body2" sx={tableCellTypographyOverflowStyles}>
												{engagementStatus.statusName}
											</Typography>
										</Box>
									) : engagementStatus ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Skeleton variant="circular" width={10} height={10} />
											<Skeleton width="60%" />
										</Box>
									) : isSkeletonLoader ? (
										<Skeleton />
									) : (
										<Typography variant="body2">{'-'}</Typography>
									);
								},
							}) as ColumnDef<EngagementDto, string>;
						case 'surveyStatus':
							return helper.accessor(
								(row) => {
									if (isLinguistLink) {
										return row.surveyStatus as SurveyStatusEnum | undefined;
									}
									const fieldObj = row.customFieldsValues?.find((el) => el.fieldName === 'Survey status');
									return fieldObj?.value as SurveyStatusEnum | undefined;
								},
								{
									header: t('engagements.columns.survey-status'),
									enableSorting: isLinguistLink ? false : isSorted,
									meta: {
										flex: 0.7,
										useCustomOverflowContainer: true,
									},
									cell: ({ getValue }) => {
										const fieldSurveyValue = getValue() as SurveyStatusEnum;

										return fieldSurveyValue && !isSkeletonLoader ? (
											<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
												<Box
													sx={{
														height: 10,
														width: 10,
														borderRadius: '50%',
														backgroundColor: SurveyStatusColorEnum[fieldSurveyValue],
														flexShrink: 0,
													}}
												></Box>
												<Typography data-overflow="true" variant="body2" sx={tableCellTypographyOverflowStyles}>
													{fieldSurveyValue}
												</Typography>
											</Box>
										) : fieldSurveyValue ? (
											<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
												<Skeleton variant="circular" width={10} height={10} />
												<Skeleton width="60%" />
											</Box>
										) : isSkeletonLoader ? (
											<Skeleton />
										) : (
											<Typography variant="body2">{'-'}</Typography>
										);
									},
								}
							) as ColumnDef<EngagementDto, string>;
						case 'isLinkedToFamily':
							return helper.accessor((row) => row.isLinkedToFamily, {
								header: t('engagements.columns.family'),
								enableSorting: isSorted,
								meta: {
									flex: 1,
									useCustomOverflowContainer: true,
								},
								cell: ({ getValue }) => {
									const value = getValue();

									return isSkeletonLoader ? (
										<Skeleton />
									) : value ? (
										<Box>
											<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
												{value}
											</Typography>
											{t('engagements.connected')}
											<VerifiedIcon sx={{ fontSize: '18px', marginLeft: '8px' }} />
										</Box>
									) : (
										<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
											{t('engagements.not-connected')}
										</Typography>
									);
								},
							}) as ColumnDef<EngagementDto, string>;

						case 'actions':
							return helper.display({
								id: 'actions',
								meta: {
									flex: 1,
								},
								cell: (props) =>
									isLinguistLink ? (
										<EngagementsLinguistLinkTableActions
											engagement={props.row.original}
											onView={onView}
											onSendReminder={onSendReminder}
											isLinguistLink={isLinguistLink}
											isAdminRoute={isAdminRoute}
											allowedEngagementActions={[]}
										/>
									) : (
										<EngagementsTableActions
											engagement={props.row.original}
											allowedEngagementActions={allowedEngagementActions}
											onDelete={onDelete}
											onEdit={onEdit}
											onView={onView}
											onSendEmail={onSendEmail}
											onSendSMS={onSendSMS}
											onArchive={onArchive}
											onUnarchive={onUnarchive}
											isLinguistLink={isLinguistLink}
											isAdminRoute={isAdminRoute}
										/>
									),
							}) as ColumnDef<EngagementDto>;

						default:
							return undefined;
					}
				})
			),
		[t, onDelete, onEdit, onView, onArchive]
	);

	const result = useMemo(
		() => [
			...(isSelectionMode
				? [
						helper.display({
							id: 'select',
							header: ({ table }) => {
								const meta = table.options.meta as any;
								const allEngagements = meta?.allFilteredEngagements ?? [];

								const selectableEngagements = (allEngagements as EngagementDto[]).filter(
									(engagement: EngagementDto) => engagement.status !== EngagementStatusEnum.Incomplete
								);

								const selectedIds = Object.keys(table.getState().rowSelection);

								const allSelected = selectableEngagements.every(({ _id }) => selectedIds.includes(_id));
								const someSelected = selectableEngagements.some(({ _id }) => selectedIds.includes(_id));

								return (
									<input
										type="checkbox"
										checked={allSelected}
										ref={(el) => {
											if (el) el.indeterminate = !allSelected && someSelected;
										}}
										onChange={() => {
											const newSelection = { ...table.getState().rowSelection };

											if (allSelected) {
												selectableEngagements.forEach(({ _id }) => {
													delete newSelection[_id];
												});
											} else {
												selectableEngagements.forEach(({ _id }) => {
													newSelection[_id] = true;
												});
											}

											table.options.onRowSelectionChange?.(newSelection);
										}}
									/>
								);
							},
							cell: ({ row }) => {
								const engagementStatus = row.original.status;

								return (
									<input
										type="checkbox"
										checked={row.getIsSelected()}
										onChange={() => {
											row.toggleSelected();
										}}
										disabled={engagementStatus === EngagementStatusEnum.Incomplete ? true : false}
									/>
								);
							},
							meta: {
								flex: 0.5,
							},
						}) as ColumnDef<EngagementDto>,
				  ]
				: []),
			...columns,
		],
		[columns, isSelectionMode]
	);

	return result;
}
